/**
 * queryTree.ts — the filter model for the Talent Pool. The UI edits a friendly `SearchState`
 * (camelCase, grouped by field); `buildTree` compiles it to the crypto-lever `QueryNode` AST the engine
 * consumes, and `treeToState` hydrates a planner/saved tree back into editable state.
 *
 * Two tiers of expressiveness:
 *   • Field sections — the common case: an AND of per-field leaves, each with its own combinator
 *     (similarity / when / match-mode) and an optional `negate` (exclude).
 *   • Boolean groups (`groups`) — AND/OR groups of conditions, optionally negated. `negate + or` = NOR.
 *     These give real boolean power (e.g. "(Python OR Go) AND NOT (intern)") and round-trip planner trees
 *     that contain OR-groups or exclusions without flattening them away.
 */

import { useCallback, useState } from 'react';
import { type Filter, type Group, type QueryNode, isGroup, emptyRoot } from '@/lib/talentPool.api';

export interface ValueSlice {
    values: string[];
    similarity: number;
    matchType: 'any' | 'all';
    when?: 'current' | 'past' | 'any';
    negate?: boolean; // exclude (NOT this field)
}
export interface Range {
    gte?: number;
    lte?: number;
}
export interface LangPick {
    language: string;
    proficiency?: string;
}

/** A single condition inside an advanced boolean group. */
export interface AdvCond {
    id?: string; // stable React key
    field: string;
    values: string[];
    similarity?: number;
    when?: 'current' | 'past' | 'any';
    gte?: number;
    lte?: number;
    negate?: boolean;
}
/** An advanced boolean group: AND/OR over conditions, optionally negated (negate + or = NOR). */
export interface AdvGroup {
    id: string;
    combinator: 'and' | 'or';
    negate: boolean;
    scope?: 'experience' | 'education' | null; // conditions co-occur in the same job/school entry
    conditions: AdvCond[];
}

export interface SearchState {
    nlQuery: string;
    criteria: string[]; // soft must-haves (scored, not hard filters) — configured separately from filters

    // value fields (similarity)
    titles: ValueSlice;
    companies: ValueSlice;
    skills: ValueSlice;
    industries: ValueSlice;
    certifications: ValueSlice;
    fieldsOfStudy: ValueSlice;
    degrees: ValueSlice;
    universities: ValueSlice; // entity-resolved
    // semantic free-text
    responsibilities: string;
    summary: string;
    name: string;

    // enum fields
    seniority: string; // floor token ('' = any) → "and up"
    seniorityExact: boolean; // match the literal level only (not "and up")
    isManager: 'any' | '1' | '0';
    departments: string[];
    studyLevel: string; // floor ('' = any) → "or above"
    studyLevelExact: boolean;
    companySizes: string[];
    jobHopper: 'any' | '1' | '0';
    languages: LangPick[];
    locationCities: string[]; // entity-resolved
    locationCountries: string[];
    educationCountries: string[];

    // range fields
    experienceYears: Range;
    avgTenure: Range;
    currentRole: Range;
    numJobs: Range;
    gradYear: Range;

    // advanced boolean groups (AND/OR/NOT/NOR over conditions)
    groups: AdvGroup[];
}

const vslice = (similarity: number, matchType: 'any' | 'all' = 'any'): ValueSlice => ({ values: [], similarity, matchType, when: 'any', negate: false });

export const initialSearchState = (): SearchState => ({
    nlQuery: '',
    criteria: [],
    titles: vslice(0.45),
    companies: vslice(0.15),
    skills: vslice(0.4, 'all'),
    industries: vslice(0.5),
    certifications: vslice(0.2),
    fieldsOfStudy: vslice(0.55),
    degrees: vslice(0.4),
    universities: vslice(0.45),
    responsibilities: '',
    summary: '',
    name: '',
    seniority: '',
    seniorityExact: false,
    isManager: 'any',
    departments: [],
    studyLevel: '',
    studyLevelExact: false,
    companySizes: [],
    jobHopper: 'any',
    languages: [],
    locationCities: [],
    locationCountries: [],
    educationCountries: [],
    experienceYears: {},
    avgTenure: {},
    currentRole: {},
    numJobs: {},
    gradYear: {},
    groups: [],
});

export const newGroup = (): AdvGroup => ({ id: gid(), combinator: 'or', negate: false, scope: null, conditions: [{ id: gid(), field: 'skill', values: [] }] });

// ── SearchState → QueryNode ──
const hasRange = (r: Range) => r.gte != null || r.lte != null;
function valueLeaf(field: string, s: ValueSlice): Filter | null {
    if (!s.values.length) return null;
    const f: Filter = { field, values: s.values, similarity: s.similarity, matchType: s.matchType };
    if (s.when && s.when !== 'any') f.when = s.when;
    if (s.negate) f.negate = true;
    return f;
}
function condToLeaf(c: AdvCond): Filter | null {
    const hasV = Array.isArray(c.values) && c.values.some((v) => String(v).trim() !== '');
    const hasR = c.gte != null || c.lte != null;
    if (!hasV && !hasR) return null;
    const f: Filter = { field: c.field };
    if (hasV) f.values = c.values;
    if (c.similarity != null) f.similarity = c.similarity;
    if (c.when && c.when !== 'any') f.when = c.when;
    if (c.gte != null) f.gte = c.gte;
    if (c.lte != null) f.lte = c.lte;
    if (c.negate) f.negate = true;
    return f;
}

export function buildTree(s: SearchState): Group {
    const root = emptyRoot();
    const push = (n: QueryNode | null) => {
        if (n) root.children.push(n);
    };

    // value fields
    push(valueLeaf('title', s.titles));
    push(valueLeaf('company', s.companies));
    push(valueLeaf('industry', s.industries));
    push(valueLeaf('certification', s.certifications));
    push(valueLeaf('field_of_study', s.fieldsOfStudy));
    push(valueLeaf('degree', s.degrees));
    push(valueLeaf('university', s.universities));
    // skills: one leaf per skill so each is independently required/semantic
    if (s.skills.values.length) {
        const neg = s.skills.negate ? { negate: true } : {};
        if (s.skills.matchType === 'all') s.skills.values.forEach((v) => push({ field: 'skill', values: [v], similarity: s.skills.similarity, ...neg }));
        else push({ field: 'skill', values: s.skills.values, similarity: s.skills.similarity, matchType: 'any', ...neg });
    }
    // semantic free-text
    if (s.responsibilities.trim()) push({ field: 'responsibilities', values: [s.responsibilities.trim()], similarity: 0.6 });
    if (s.summary.trim()) push({ field: 'summary', values: [s.summary.trim()], similarity: 0.6 });
    if (s.name.trim()) push({ field: 'name', values: [s.name.trim()], similarity: 0.3 });

    // enum fields
    if (s.seniority) push({ field: 'seniority', values: [s.seniority], ...(s.seniorityExact ? { exactLevels: true } : {}) });
    if (s.isManager !== 'any') push({ field: 'is_manager', values: [s.isManager] });
    if (s.departments.length) push({ field: 'department', values: s.departments, matchType: 'any' });
    if (s.studyLevel) push({ field: 'study_level', values: [s.studyLevel], ...(s.studyLevelExact ? { exactLevels: true } : {}) });
    if (s.companySizes.length) push({ field: 'company_size', values: s.companySizes, matchType: 'any' });
    if (s.jobHopper !== 'any') push({ field: 'job_hopper', values: [s.jobHopper] });
    s.languages.forEach((l) =>
        push({ field: 'language', values: [l.language], ...(l.proficiency && l.proficiency !== 'any' ? { proficiency: l.proficiency } : {}) }),
    );
    if (s.locationCities.length) push({ field: 'location_city', values: s.locationCities, matchType: 'any' });
    if (s.locationCountries.length) push({ field: 'location_country', values: s.locationCountries, matchType: 'any' });
    if (s.educationCountries.length) push({ field: 'education_country', values: s.educationCountries, matchType: 'any' });

    // range fields
    const range = (field: string, r: Range) => {
        if (hasRange(r)) push({ field, ...(r.gte != null ? { gte: r.gte } : {}), ...(r.lte != null ? { lte: r.lte } : {}) });
    };
    range('experience_years', s.experienceYears);
    range('avg_tenure', s.avgTenure);
    range('current_role', s.currentRole);
    range('num_jobs', s.numJobs);
    range('grad_year', s.gradYear);

    // advanced boolean groups
    for (const g of s.groups) {
        const children = g.conditions.map(condToLeaf).filter((c): c is Filter => c != null);
        if (children.length) root.children.push({ type: 'group', combinator: g.combinator, negate: g.negate, ...(g.scope ? { scope: g.scope } : {}), children });
    }

    return root;
}

// ── QueryNode → SearchState (hydrate from /plan or a saved search) ──
const VALUE_SLICE_FIELDS: Record<string, keyof SearchState> = {
    title: 'titles',
    company: 'companies',
    industry: 'industries',
    certification: 'certifications',
    field_of_study: 'fieldsOfStudy',
    degree: 'degrees',
    university: 'universities',
};

export function gid() {
    return Math.random().toString(36).slice(2, 9);
}

function collectLeaves(n: QueryNode, out: Filter[] = []): Filter[] {
    if (isGroup(n)) n.children.forEach((c) => collectLeaves(c, out));
    else out.push(n);
    return out;
}
function leafToCond(f: Filter): AdvCond {
    return { id: gid(), field: f.field, values: f.values ?? [], similarity: f.similarity, when: f.when, gte: f.gte, lte: f.lte, negate: f.negate };
}

/** Apply a top-level LEAF to the friendly field sections. */
function applyLeaf(f: Filter, s: SearchState) {
    const vals = f.values || [];
    const k = VALUE_SLICE_FIELDS[f.field];
    if (k) {
        const cur = s[k] as ValueSlice;
        s[k] = {
            ...cur,
            values: [...cur.values, ...vals],
            similarity: f.similarity ?? cur.similarity,
            matchType: f.matchType ?? cur.matchType,
            when: (f.when as ValueSlice['when']) ?? cur.when,
            negate: cur.negate || !!f.negate,
        } as never;
        return;
    }
    switch (f.field) {
        case 'skill':
            // buildTree encodes the combinator structurally: matchType:'any' tags one multi-value leaf;
            // 'all' fans out to untagged single-value leaves. Reconstruct it, else 'any' reverts to 'all'.
            s.skills = {
                ...s.skills,
                values: [...s.skills.values, ...vals],
                similarity: f.similarity ?? s.skills.similarity,
                matchType: f.matchType === 'any' ? 'any' : s.skills.matchType,
                negate: s.skills.negate || !!f.negate,
            };
            break;
        case 'responsibilities':
            s.responsibilities = vals[0] || s.responsibilities;
            break;
        case 'summary':
            s.summary = vals[0] || s.summary;
            break;
        case 'name':
            s.name = vals[0] || s.name;
            break;
        case 'seniority':
            s.seniority = vals[0] || s.seniority;
            if (f.exactLevels != null) s.seniorityExact = !!f.exactLevels;
            break;
        case 'is_manager':
            s.isManager = (vals[0] as SearchState['isManager']) || s.isManager;
            break;
        case 'department':
            s.departments = [...s.departments, ...vals];
            break;
        case 'study_level':
            s.studyLevel = vals[0] || s.studyLevel;
            if (f.exactLevels != null) s.studyLevelExact = !!f.exactLevels;
            break;
        case 'company_size':
            s.companySizes = [...s.companySizes, ...vals];
            break;
        case 'job_hopper':
            s.jobHopper = (vals[0] as SearchState['jobHopper']) || s.jobHopper;
            break;
        case 'language':
            for (const v of vals) if (v) s.languages = [...s.languages, { language: v, proficiency: f.proficiency }];
            break;
        case 'location_city':
            s.locationCities = [...s.locationCities, ...vals];
            break;
        case 'location_country':
            s.locationCountries = [...s.locationCountries, ...vals];
            break;
        case 'education_country':
            s.educationCountries = [...s.educationCountries, ...vals];
            break;
        case 'experience_years':
            s.experienceYears = { gte: f.gte, lte: f.lte };
            break;
        case 'avg_tenure':
            s.avgTenure = { gte: f.gte, lte: f.lte };
            break;
        case 'current_role':
            s.currentRole = { gte: f.gte, lte: f.lte };
            break;
        case 'num_jobs':
            s.numJobs = { gte: f.gte, lte: f.lte };
            break;
        case 'grad_year':
            s.gradYear = { gte: f.gte, lte: f.lte };
            break;
    }
}

/**
 * A flat-root LEAF the friendly sections can't faithfully hold, so applying it would silently lose data.
 * The flat `SearchState` has no temporal axis on enum/range/array fields — a `when:past` (or a manager
 * now/IC-before contrast) collapses into a single value with the scope stripped, turning "manager now,
 * IC before" into a self-contradictory "manager AND IC, both now". Such leaves are diverted to an
 * advanced group instead (AdvCond carries `when`, condToLeaf re-emits it), so the round-trip is lossless.
 * ValueSlice fields (title, skill…) already preserve `when` via the slice, so they're never lossy here.
 */
function lossyTemporalLeaf(f: Filter, managerContrast: boolean): boolean {
    if (VALUE_SLICE_FIELDS[f.field] || f.field === 'skill') return false;
    if (f.when === 'past') return true;
    if (f.field === 'is_manager' && f.when === 'current' && managerContrast) return true;
    return false;
}

export function treeToState(tree: QueryNode, base?: SearchState): SearchState {
    const s = base ? { ...base } : initialSearchState();
    const children = isGroup(tree) ? tree.children : [tree];
    // Pre-scan for an is_manager temporal contrast (now vs. before) so both halves are kept as groups.
    const flatManagerWhens = children.filter((c): c is Filter => !isGroup(c) && c.field === 'is_manager').map((c) => c.when ?? 'current');
    const managerContrast = flatManagerWhens.includes('past') && (flatManagerWhens.includes('current') || flatManagerWhens.length > 1);
    for (const child of children) {
        if (isGroup(child)) {
            // a planner OR-group / negated group / scope group → keep it as an advanced boolean group
            const conds = collectLeaves(child).map(leafToCond);
            if (conds.length) {
                s.groups = [
                    ...s.groups,
                    { id: gid(), combinator: child.combinator === 'or' ? 'or' : 'and', negate: !!child.negate, scope: child.scope ?? null, conditions: conds },
                ];
            }
        } else if (lossyTemporalLeaf(child, managerContrast)) {
            // a temporal enum/range leaf the flat sections would flatten → keep it as a 1-condition group
            s.groups = [...s.groups, { id: gid(), combinator: 'and', negate: !!child.negate, scope: null, conditions: [leafToCond({ ...child, negate: false })!] }];
        } else if (child.negate && !VALUE_SLICE_FIELDS[child.field] && child.field !== 'skill') {
            // a negated non-value-slice leaf → a NOT group, so the exclusion survives the round-trip
            s.groups = [...s.groups, { id: gid(), combinator: 'and', negate: true, scope: null, conditions: [leafToCond({ ...child, negate: false })!] }];
        } else {
            applyLeaf(child, s);
        }
    }
    return s;
}

/** count of active hard filters (field leaves + boolean groups). */
export function activeCount(s: SearchState): number {
    return buildTree(s).children.length;
}

// ── controller hook ──
export function useSearchState(init?: SearchState) {
    const [state, setState] = useState<SearchState>(init ?? initialSearchState());
    const set = useCallback(<K extends keyof SearchState>(k: K, v: SearchState[K]) => setState((s) => ({ ...s, [k]: v })), []);
    const setSlice = useCallback(
        (k: keyof SearchState, patch: Partial<ValueSlice>) => setState((s) => ({ ...s, [k]: { ...(s[k] as ValueSlice), ...patch } })),
        [],
    );
    const hydrate = useCallback(
        (tree: QueryNode, criteria: string[], nlQuery: string) => setState({ ...treeToState(tree, initialSearchState()), nlQuery, criteria }),
        [],
    );
    return { state, setState, set, setSlice, hydrate };
}
export type SearchCtl = ReturnType<typeof useSearchState>;
